import { GetMarketTradingCalendarResponse, TradingCalendarEntry } from '@flipster/apis/v2/market/types';
import { InstrumentApi } from '@flipster/apis/v2/snapshot/types';
import { timestampToDate } from '@flipster/utils/datetime';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';

import { isTradFiInstrument } from '../rwa';

/**
 * now 시각이 속한 캘린더 엔트리의 인덱스를 반환한다.
 * 엔트리는 timestamp 오름차순 정렬이라고 가정한다. 인터벌 규칙: [start, end) — 경계값은 다음 엔트리에 속함.
 * now가 첫 엔트리 시작 이전이면 -1.
 */
export const findCurrentEntryIndex = (entries: TradingCalendarEntry[], now: number): number => {
  let lo = 0;
  let hi = entries.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (timestampToDate(entries[mid].timestamp).getTime() <= now) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
};

/**
 * 현재 시각이 Tradable off-hours 세션에 속하는지 여부.
 * trading-calendar API의 `isOffHours` 필드로 판정한다 (specs 아님, ERD-240 §8).
 *
 * - 엔트리 없음 / 첫 엔트리 이전(인덱스 -1) → false
 * - 마지막 엔트리 구간(다음 경계 없음) → false (isDateOutsideTradingHours와 동일한 마지막 엔트리 무시 규칙)
 * - `isOffHours`가 없는 구버전 응답 → false (하위호환)
 */
export const isCurrentlyOffHours = (entries: TradingCalendarEntry[], now: number): boolean => {
  if (entries.length === 0) return false;
  const idx = findCurrentEntryIndex(entries, now);
  if (idx < 0 || !entries[idx + 1]) return false;
  return entries[idx].isOffHours === true;
};

/**
 * 심볼이 현재 "Tradable off-hours" 상태인지 판정한다 (Off Hours Alert / 경고 노티스 대상).
 *
 * Tradable off-hours = off-hours 세션이면서 현재 세션이 거래 가능(tradableMarketSessions 포함).
 * non-tradable off-hours(서스펜션)는 기존 Trading Hours 서스펜션 로직이 우선하므로 여기서는 false.
 *
 * - TradFi 심볼이 아니면 false (crypto 무관)
 * - tradingCalendar가 없거나 TRADING_CALENDAR_247이면 false
 * - 캘린더 데이터 미로드 / 해당 캘린더 키 없음 → false
 */
export const isTradableOffHours = (
  symbolInfo: InstrumentApi | undefined | null,
  calendarData: GetMarketTradingCalendarResponse | undefined | null,
  now: number,
): boolean => {
  if (!isTradFiInstrument(symbolInfo)) return false;
  const calendarKey = symbolInfo?.tradingCalendar;
  if (!calendarData || !calendarKey || calendarKey === 'TRADING_CALENDAR_247') return false;
  const entries = calendarData[calendarKey];
  if (!entries || !isCurrentlyOffHours(entries, now)) return false;
  const tradableSessions = symbolInfo?.tradableMarketSessions ?? [];
  return tradableSessions.includes(entries[findCurrentEntryIndex(entries, now)].status);
};

/**
 * Off Hours Alert가 필요한 액션 (백엔드가 거절할 수 있는 요청 — ERD-240 §8.2).
 * - openOrder: 주문폼 Long/Short (non reduce-only)
 * - orderModify: pending 주문 수정
 */
export const offHoursAlertRequiredActions = [
  'openOrder',
  'orderModify',
  'addPosition',
  'adjustMargin',
  'adjustLeverage',
  'flip',
] as const;

/**
 * Off Hours Alert가 불필요한 액션 (백엔드가 정책상 수용 — ERD-240 §8.2).
 * 한도 초과 시 백엔드가 거절하면 기존 에러 토스트 경로로 처리된다.
 */
export const offHoursAlertExemptActions = [
  'positionClose',
  'reduceOnlyOrder',
  'positionTpsl',
  'pendingCancel',
  'pendingTpslModify',
] as const;

export type OffHoursAction =
  | (typeof offHoursAlertRequiredActions)[number]
  | (typeof offHoursAlertExemptActions)[number];

export const isOffHoursAlertRequiredAction = (action: OffHoursAction): boolean =>
  (offHoursAlertRequiredActions as readonly string[]).includes(action);

export interface ShouldShowOffHoursAlertParams {
  /** 현재 Tradable off-hours 상태인지 (isTradableOffHoursAtom) */
  isTradableOffHours: boolean;
  /** Off Hours Alert 피처 플래그 (Amplitude experiment) 활성 여부 */
  isFeatureEnabled: boolean;
  /** 사용자가 "Don't show again"을 선택했는지 */
  dontShowAgain: boolean;
  action: OffHoursAction;
}

/**
 * 주어진 액션에 대해 Off Hours Alert를 표시해야 하는지 판정한다.
 * Tradable off-hours + 피처 활성 + Don't show again 미설정 + Alert 필요 액션일 때만 true.
 */
export const shouldShowOffHoursAlert = ({
  isTradableOffHours,
  isFeatureEnabled,
  dontShowAgain,
  action,
}: ShouldShowOffHoursAlertParams): boolean => {
  if (!isFeatureEnabled || !isTradableOffHours || dontShowAgain) return false;
  return isOffHoursAlertRequiredAction(action);
};

const offHoursAlertSkipKey = 'flipster.configs.v1.skipOffHoursAlert';

/** 사용자가 Off Hours Alert "Don't show again"을 선택했는지 (디바이스 저장, 기본값 false=표시) */
export const getOffHoursAlertDontShowAgain = (): boolean => getLocalStorage(offHoursAlertSkipKey) === true;

export const setOffHoursAlertDontShowAgain = (dontShowAgain: boolean): void =>
  setLocalStorage(offHoursAlertSkipKey, dontShowAgain);
